import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Volume2, VolumeX, Link2 } from "lucide-react";
import { toast } from "sonner";
import VoteChevron from "./VoteChevron";
import Lightbox from "./Lightbox";

/**
 * A community showcase — either the hero (page 1 rank 1) or a tile
 * (all other positions). Layout is driven by the `variant` prop:
 *
 *   variant="hero"   large, full-bleed image + collector row + big vote
 *   variant="tile"   compact card in a 12-column grid
 *
 * Media handling:
 *   • Image entries: click opens the lightbox with the FULL-RES source
 *     (loaded on demand). Thumbnail is used for the tile / hero preview.
 *   • Video entries (transcode.ready): inline <video muted loop autoplay
 *     playsInline>; tap the speaker chip to unmute. Click the media to
 *     open the lightbox with a normal <video controls>.
 *   • Fallback: reference image (used by the seed script so gallery
 *     tiles are never blank in preview).
 *
 * Crawlability (owner directive, showcase pass 2026-02-01):
 *   • When `permalink` is provided the whole media frame is wrapped in
 *     a REAL <a href={permalink}> so crawlers, middle-click, cmd-click,
 *     and "open link in new tab" all resolve to the showcase page.
 *   • When `openLightboxOnClick` is true (default — card page behavior)
 *     a plain LEFT click is intercepted (preventDefault) to open the
 *     lightbox instead of navigating. Modifier-clicked / middle-clicked
 *     events pass through the anchor as normal.
 *   • When `openLightboxOnClick` is false (profile gallery) left-clicks
 *     just navigate.
 *   • A small permalink icon in the corner (`showcase-permalink-*`)
 *     always navigates to the showcase page and copies to clipboard on
 *     shift-click.
 */
export default function Showcase({
  entry,
  variant = "tile",
  viewer,
  onVoteChange,
  permalink = null,
  openLightboxOnClick = true,
}) {
  const [lightbox, setLightbox] = useState(null);
  const [muted, setMuted] = useState(true);
  const videoRef = useRef(null);
  const hasVideo = entry.video && entry.video.transcode_status === "ready";
  const hasImage = !!entry.image;

  const previewSrc = pickPreview(entry);
  const rankBadge = entry.rank != null ? `#${entry.rank}` : null;

  function openLightbox() {
    if (hasVideo) {
      setLightbox({
        kind: "video",
        src: entry.video.mp4_url || entry.video.preview_url,
        poster: entry.video.poster_url,
      });
    } else if (hasImage) {
      setLightbox({ kind: "image", src: entry.image.url });
    }
  }

  /** Handle a click on the anchor wrapping the media. */
  function handleAnchorClick(e) {
    // Modifier-clicks / middle-clicks / right-clicks pass through the
    // anchor's normal behavior (open in new tab / copy link / etc.).
    if (
      e.button !== 0 ||
      e.metaKey || e.ctrlKey || e.shiftKey || e.altKey
    ) {
      return;
    }
    if (openLightboxOnClick) {
      e.preventDefault();
      openLightbox();
    }
    // else: let the anchor navigate (React Router will pick up the URL
    // on a full page load if we're just using <a href>).
  }

  /** Small permalink chip — always navigates, copies on shift-click. */
  function handlePermalinkClick(e) {
    if (e.shiftKey && typeof navigator !== "undefined" && permalink) {
      e.preventDefault();
      const absolute = typeof window !== "undefined"
        ? new URL(permalink, window.location.origin).toString()
        : permalink;
      navigator.clipboard.writeText(absolute)
        .then(() => toast.success("Link copied"))
        .catch(() => toast.error("Couldn't copy"));
    }
    // else: normal navigation via <Link>.
  }

  const CollectorLine = (
    <div className="mt-3 flex flex-col gap-2 text-meta text-ink-muted">
      <Link
        data-testid={`showcase-user-${entry.id}`}
        to={`/users/${entry.user_id}`}
        title={entry.user?.display_name || entry.user_display_name || "Collector"}
        className="truncate text-ink-text hover:text-indigo-text"
      >
        {entry.user?.display_name || entry.user_display_name || "Collector"}
      </Link>
      <div className="flex items-center justify-between gap-2">
        {rankBadge && (
          <span
            data-testid={`showcase-rank-${entry.id}`}
            className="inline-flex shrink-0 items-center rounded-chip border border-ink-chip bg-ink-raised px-2 py-0.5 text-eyebrow uppercase text-ink-muted tabular"
          >
            RANK {entry.rank}
          </span>
        )}
        <VoteChevron
          submission={entry}
          viewer={viewer}
          onChange={onVoteChange}
          size={variant === "hero" ? "lg" : "md"}
        />
      </div>
    </div>
  );

  const mediaFrame = (
    <MediaFrame
      entry={entry}
      previewSrc={previewSrc}
      hasVideo={hasVideo}
      muted={variant === "hero" ? muted : true}
      videoRef={videoRef}
      testIdRoot={variant === "hero" ? "showcase-hero-media" : "showcase-tile-media"}
      aspect="portrait"
    />
  );

  // The whole media frame is a real <a> so middle-click / cmd-click /
  // crawlers work. On a plain left click we intercept if the parent
  // wants lightbox behavior.
  const wrappedMedia = permalink ? (
    <a
      href={permalink}
      onClick={handleAnchorClick}
      className="block cursor-zoom-in outline-none focus:outline focus:outline-2 focus:outline-indigo-text"
      data-testid={`showcase-permalink-anchor-${entry.id}`}
    >
      {mediaFrame}
    </a>
  ) : (
    <div
      role="button"
      tabIndex={0}
      onClick={openLightbox}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && openLightbox()}
      className="block cursor-zoom-in"
    >
      {mediaFrame}
    </div>
  );

  const permalinkAffordance = permalink ? (
    <Link
      to={permalink}
      data-testid={`showcase-permalink-${entry.id}`}
      onClick={handlePermalinkClick}
      title="Open showcase page (shift-click to copy link)"
      aria-label="Open showcase page"
      className="absolute left-3 top-3 rounded-chip border border-ink-chip bg-ink-bg/75 p-1.5 text-ink-muted backdrop-blur-sm transition-colors duration-cc ease-cc-ease hover:text-ink-text"
    >
      <Link2 size={14} />
    </Link>
  ) : null;

  if (variant === "hero") {
    return (
      <>
        <article
          data-testid={`showcase-hero-${entry.id}`}
          className="animate-rise mx-auto w-full max-w-[720px] overflow-hidden rounded-card border border-ink-chip bg-ink-surface"
        >
          <div className="relative">
            {wrappedMedia}
            {permalinkAffordance}
            {hasVideo && (
              <button
                data-testid={`showcase-hero-mute-${entry.id}`}
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); setMuted((m) => !m); if (videoRef.current) videoRef.current.muted = !muted; }}
                className="absolute right-3 top-3 rounded-chip border border-ink-chip bg-ink-bg/75 p-1.5 text-ink-muted backdrop-blur-sm hover:text-ink-text"
                aria-label={muted ? "Unmute" : "Mute"}
              >
                {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
              </button>
            )}
          </div>
          <div className="px-5 py-4">
            {CollectorLine}
          </div>
        </article>
        <Lightbox open={!!lightbox} item={lightbox} onClose={() => setLightbox(null)} />
      </>
    );
  }

  // ---- tile variant ----
  return (
    <>
      <article
        data-testid={`showcase-tile-${entry.id}`}
        className="animate-rise overflow-hidden rounded-card border border-ink-chip bg-ink-surface"
      >
        <div className="relative">
          {wrappedMedia}
          {permalinkAffordance}
        </div>
        <div className="px-3 pb-3 pt-2">
          {CollectorLine}
        </div>
      </article>
      <Lightbox open={!!lightbox} item={lightbox} onClose={() => setLightbox(null)} />
    </>
  );
}

function MediaFrame({ entry, previewSrc, hasVideo, muted, videoRef, testIdRoot, aspect }) {
  const ratio = aspect === "landscape" ? "16 / 9" : "5 / 7";
  if (hasVideo) {
    return (
      <div
        className="h-full w-full"
        style={{ aspectRatio: ratio }}
        data-testid={`${testIdRoot}-${entry.id}`}
      >
        <video
          ref={videoRef}
          src={entry.video.preview_url || entry.video.mp4_url}
          poster={entry.video.poster_url}
          muted={muted}
          autoPlay
          loop
          playsInline
          preload="metadata"
          className="h-full w-full object-cover"
        />
      </div>
    );
  }
  return (
    <div
      className="h-full w-full"
      style={{ aspectRatio: ratio }}
      data-testid={`${testIdRoot}-${entry.id}`}
    >
      {previewSrc ? (
        <img
          src={previewSrc}
          alt={
            entry.card_name
              ? `${entry.card_name}${entry.card_number ? ` ${entry.card_number}` : ""}${entry.card_variant_label ? ` ${entry.card_variant_label}` : ""} — collector showcase`
              : "collector showcase"
          }
          loading="lazy"
          className="h-full w-full object-cover"
        />
      ) : (
        <div className="cc-card-plate h-full w-full">no media</div>
      )}
    </div>
  );
}

function pickPreview(entry) {
  if (entry.image) {
    return entry.image.thumbnail_url || entry.image.url;
  }
  if (entry.video?.poster_url) return entry.video.poster_url;
  return null;
}
